import get from 'lodash.get';

import React, { useState, useEffect } from 'react';
import { flowRight as compose } from 'lodash';
import { intlShape, injectIntl } from 'react-intl';
import moment from 'moment-timezone';
import BannerThree from '../../components/BannerThree';
import PageWrapper from '../../components/Utility/PageWrapper';
import AmenityList from '../../components/AmenityList/AmenityList';
import ContentDivider from '../../components/BuildingBlocks/ContentDivider/ContentDivider';
import Grid from '../../components/Utility/Grid';
import VerticalSpacing from '../../components/Utility/VerticalSpacing';
import Text from '../../components/BuildingBlocks/Text';
import Affiliations from '../../components/Affiliations';

import CardGridLayout from '../../components/CardGridLayout';
import PDPContent from '../../components/PropertyDetailContent/PDPContent';
import RoomBookingCollection from '../../components/RoomBookingCollection';
import LocaleLink from '../../components/LocaleLink';

import ErrorPage from '../../components/ErrorPage';
import PageSEO from '../../components/PageSEO';
import Placeholder from '../../components/Placeholder';
import PropertyMap from '../../components/PropertyMap';

import withAnalytics, { withAnalyticsPropType } from '../../hocs/withAnalytics';
import withLocaleProvider from '../../hocs/withLocaleProvider';
import withRouting, { withRoutingPropType } from '../../hocs/withRouting';
import withAuth, { withAuthPropType } from '../../hocs/withAuth';

import PropertyDetailsContainer from '../../components/PropertyDetails/PropertyDetailsContainer';
import BookNowContainer from '../../components/PropertyDetails/BookNowContainer';

import messages from '../../messages/PropertyDetails/messages';
import {
  PropertyMapWrapper,
  Sticky,
  StickyEnd,
  StickyStart,
  InfoContainer,
  ErrorText
} from '../../styles/PropertyDetails/styles';
import PropertyAvailability from '../../constants/enums/PropertyAvailability';
import ORGANIZATION_PERMISSIONS from '../../constants/enums/OrganizationPermission';
import { hasOrganizationPermission } from '../../helpers/permissionHelper';
import dispatchToast from '../../components/ToastComponent';
import { TOAST_TYPE } from '../../components/ToastComponent/lib';

function PropertyDetails({ analytics, intl, router, auth }) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);

    analytics.trackRoute('Property Details');
  }, [analytics]);

  const renderDetails = (property, wrapWithContentDivider = true) => {
    const propertyDetailComponent = (
      <PDPContent
        location={property.location}
        name={property.name}
        description={property.description}
        openText={intl.formatMessage(messages.seeMore)}
        closeText={intl.formatMessage(messages.seeLess)}
      />
    );

    if (wrapWithContentDivider) {
      return (
        <ContentDivider after theme="hr">
          {propertyDetailComponent}
        </ContentDivider>
      );
    }

    return propertyDetailComponent;
  };

  const renderAmenities = property => {
    if (property.amenities.length === 0) {
      return null;
    }

    return (
      <ContentDivider after theme="hr">
        <AmenityList
          amenities={property.amenities}
          title={intl.formatMessage(messages.amenities)}
          openText={intl.formatMessage(messages.seeMore)}
          closeText={intl.formatMessage(messages.seeLess)}
        />
      </ContentDivider>
    );
  };

  const renderLocation = property => (
    <VerticalSpacing size="sm">
      {/* Heading */}
      <Text.HeadingFourth tagName="h3">{intl.formatMessage(messages.location)}</Text.HeadingFourth>

      {/* Map/Address */}
      <PropertyMapWrapper>
        <PropertyMap propertyInfo={property} />
      </PropertyMapWrapper>
    </VerticalSpacing>
  );

  const renderAffiliations = property => {
    // Do not render affiliations section if there are none
    if (property.affiliations.length === 0) {
      return null;
    }

    return (
      <ContentDivider before theme="hr">
        <Affiliations
          closeText={intl.formatMessage(messages.seeLess)}
          content={property.affiliations}
          openText={intl.formatMessage(messages.seeMore)}
          title={intl.formatMessage(messages.affiliations)}
        />
      </ContentDivider>
    );
  };

  const dispatchErrorToast = errorMessage => {
    const message = intl.formatMessage(errorMessage);
    dispatchToast(TOAST_TYPE.ERROR, intl.formatMessage(messages.errorMessageToast, { message }));
  };

  const renderBookNow = (property, bookNowProps) => {
    const authenticated = get(auth, 'authenticated', false);

    const currentTimeEdmonton = moment().tz('America/Edmonton');
    const expiry = moment.tz('2025-01-01', 'America/Edmonton');
    const isBeforeExpiry = currentTimeEdmonton.isBefore(expiry);

    const shouldDisplayAlbertaTaxNotification = property?.address?.region === 'Alberta' && isBeforeExpiry;

    const {
      initiateBooking,
      checkin,
      checkout,
      numRooms,
      changeDates,
      changeNumRooms,
      changeRoomCount,
      getRoomCount,
      estimatedCost,
      currencyType,
      capacity,
      totalRoomCount,
      isRoomAvailable,
      rooms,
      nights,
      roomInputs,
      ratesLoading,
      propertyConnected,
      bookingBypassReasonId,
      toggleBlockBooking,
      showBlockBookingToggle,
      isBlockBooking
    } = bookNowProps;

    const { timeZone } = property;
    const canCreateBooking = hasOrganizationPermission(auth, ORGANIZATION_PERMISSIONS.CREATE_BOOKING);
    const canCreateMultiGuestBooking = hasOrganizationPermission(
      auth,
      ORGANIZATION_PERMISSIONS.CREATE_MULTI_GUEST_BOOKING
    );
    // Only render if mounted in a browser not SSR
    // Render nothing if the property has no rooms
    if (!isMounted || rooms.length === 0) {
      return null;
    }

    let restrictPastCheckin = false;
    const isPastSelected = moment.tz(checkin, timeZone).isBefore(moment.tz(timeZone).startOf('day'));

    if (isPastSelected) {
      restrictPastCheckin = true;
    }

    return (
      <>
        {!authenticated && (
          <InfoContainer>
            <Text.HeadingFourth tagName="div" color="primary">
              {intl.formatMessage(messages.infoForNotLoggedInUserPart1)}{' '}
              <Text.HeadingFourth tagName="span" color="quaternary" noMargins>
                <LocaleLink route="SignIn" passHref>
                  <a href="#">{intl.formatMessage(messages.signIn)}</a>
                </LocaleLink>
              </Text.HeadingFourth>{' '}
              {' or '}
              <Text.HeadingFourth tagName="span" color="quaternary" noMargins>
                <LocaleLink route="SignUp" passHref>
                  <a href="#">{intl.formatMessage(messages.joinNow)}</a>
                </LocaleLink>
              </Text.HeadingFourth>{' '}
              {intl.formatMessage(messages.infoForNotLoggedInUserPart2)}
            </Text.HeadingFourth>
          </InfoContainer>
        )}

        {authenticated && (
          <>
            <RoomBookingCollection.Container
              dates={{
                start: checkin,
                end: checkout
              }}
              numRooms={numRooms}
              onChangeDates={changeDates}
              onChangeRooms={changeNumRooms}
              title={intl.formatMessage(messages.bookNow)}
              loading={ratesLoading}
            >
              {restrictPastCheckin && (
                <div>
                  <ErrorText>{intl.formatMessage(messages.pastDateSelected, { message: timeZone })}</ErrorText>
                  <ErrorText>{intl.formatMessage(messages.pastDateCallToAction)}</ErrorText>
                </div>
              )}
              {rooms.map(room => {
                /**
                 * Temporary fix to show connected properties as Sold Out
                 * when room.availability returned from services as Undefined / Null
                 */
                if (propertyConnected) {
                  room.availability = room.availability || PropertyAvailability.SOLD_OUT;
                  // isRoomAvailable(rooms);
                } else if (!room.rate.value) {
                  room.availability = PropertyAvailability.SOLD_OUT;
                }
                // end of temporary fix

                // identify if ceilingRate is available
                const ceilingIsAvailable = room.ceilingRate !== null;

                if (!auth.me || !auth.me.admin) {
                  return (
                    <RoomBookingCollection.Panel
                      loading={ratesLoading}
                      key={room.id}
                      roomId={room.id}
                      roomInputs={roomInputs}
                      name={room.name}
                      capacity={room.guestsPerRoom}
                      price={room.availability === PropertyAvailability.SOLD_OUT ? 'N/A' : room.rate.formatted}
                      availableRooms={room.numRoomsAvail}
                      availability={room.availability}
                      openText={intl.formatMessage(messages.hideRoomDetails)}
                      closeText={intl.formatMessage(messages.showRoomDetails)}
                      open={false}
                      currency={
                        // no ceiling check is necessary here
                        room.rate.value && room.availability !== PropertyAvailability.SOLD_OUT
                          ? intl.formatMessage(messages.perNightRate, {
                              name: room.rate.name
                            })
                          : ''
                      }
                      needsContactUsLink={!room.rate.value || false}
                      roomDescription={room.description}
                      onRoomsChange={
                        room.rate.value
                          ? value => {
                              changeRoomCount(room.id, value);
                            }
                          : () => {}
                      }
                      capacityText={intl.formatMessage(messages.capacity)}
                      startingText={
                        room.rate.value
                          ? intl.formatMessage(messages.averageRate)
                          : intl.formatMessage(messages.contactUs)
                      }
                      totalRoomCount={totalRoomCount}
                      roomsValue={getRoomCount(room.id)}
                      strikeoutPrice={ceilingIsAvailable ? null : room.strikeoutRate}
                      callForPricing={room.callForPricing || room.rate === null || room.rate === undefined}
                      nightlyRates={room.nightlyRates}
                      callForPricingLabel={intl.formatMessage(messages.callForAvailability)}
                      callForPricingPhoneNumber={intl.formatMessage(messages.contactUs)}
                      isAdmin={false}
                      canCreateBooking={canCreateBooking}
                      canCreateMultiGuestBooking={canCreateMultiGuestBooking}
                    />
                  );
                }
                return (
                  <RoomBookingCollection.Panel
                    loading={ratesLoading}
                    key={room.id}
                    roomId={room.id}
                    roomInputs={roomInputs}
                    name={room.name}
                    capacity={room.guestsPerRoom}
                    availableRooms={room.numRoomsAvail}
                    availability={room.availability}
                    price={room.rate.adminValues.formatted}
                    openText={intl.formatMessage(messages.hideRoomDetails)}
                    closeText={intl.formatMessage(messages.showRoomDetails)}
                    open={false}
                    currency={intl.formatMessage(messages.perNightRate, {
                      name: get(room, 'rate.adminValues.name')
                    })}
                    totalRoomCount={totalRoomCount}
                    needsContactUsLink={false}
                    roomDescription={room.description}
                    onRoomsChange={value => {
                      changeRoomCount(room.id, value);
                    }}
                    capacityText={intl.formatMessage(messages.capacity)}
                    startingText={
                      room.rate.callForPricing
                        ? intl.formatMessage(messages.estimatedRate)
                        : intl.formatMessage(messages.averageRate)
                    }
                    roomsValue={getRoomCount(room.id)}
                    strikeoutPrice={ceilingIsAvailable ? null : room.strikeoutRate}
                    callForPricing={room.rate.callForPricing || room.rate === null || room.rate === undefined}
                    nightlyRates={room.nightlyRates}
                    callForPricingLabel={intl.formatMessage(messages.callForPricing)}
                    callForAvailabilityLabel={intl.formatMessage(messages.callForAvailability)}
                    callForPricingPhoneNumber={intl.formatMessage(messages.contactUs)}
                    bookingBypassReasonId={bookingBypassReasonId}
                    isAdmin
                    canCreateBooking={canCreateBooking}
                    canCreateMultiGuestBooking={canCreateMultiGuestBooking}
                  />
                );
              })}
            </RoomBookingCollection.Container>

            {/*
          The booking summary will start to stick when the bottom of the window
          reaches this element it also leaves some extra space in the page so
          the contents of the page don't jump around when the nav becomes sticky.
        */}
            <StickyStart>
              {/* Sticks and unsticks the booking summary. */}
              <Sticky>
                <RoomBookingCollection.Summary
                  totalPrice={intl
                    .formatNumber(estimatedCost || 0, {
                      style: 'currency',
                      currency: currencyType,
                      currencyDisplay: 'symbol'
                    })
                    .replace(/us|ca|a|/gi, '')}
                  capacity={capacity}
                  numRooms={numRooms}
                  isRoomAvailable={isRoomAvailable}
                  rooms={totalRoomCount}
                  nights={nights}
                  onClick={initiateBooking}
                  bookingBypassReasonId={bookingBypassReasonId}
                  restrictPastCheckin={restrictPastCheckin}
                  isBlockBooking={isBlockBooking}
                  toggleBlockBooking={toggleBlockBooking}
                  showBlockBookingToggle={showBlockBookingToggle}
                  labels={{
                    bookingButton: intl.formatMessage(messages.bookNow).toUpperCase(),
                    capacity: intl.formatMessage(messages.capacity),
                    template: 'You have {rooms} {roomLabel} selected for {nights} {nightLabel}',
                    estimatedCost: intl.formatMessage(messages.estimatedCost),
                    currency: currencyType,
                    mobileCurrency: currencyType,
                    singularRoom: intl.formatMessage(messages.room),
                    multipleRooms: intl.formatMessage(messages.roomPlural),
                    singularNight: intl.formatMessage(messages.night),
                    multipleNights: intl.formatMessage(messages.nightPlural),
                    maxOccupancyNote: intl.formatMessage(messages.maxOccupancyNote),
                    blockBooking: intl.formatMessage(messages.blockBookingText),
                    permissionDenied: intl.formatMessage(messages.permissionDenied),
                    restrictedRooms: intl.formatMessage(messages.restrictedRooms)
                  }}
                  canCreateBooking={canCreateBooking}
                  canCreateMultiGuestBooking={canCreateMultiGuestBooking}
                  shouldDisplayAlbertaTaxNotification={shouldDisplayAlbertaTaxNotification}
                />
              </Sticky>
            </StickyStart>
          </>
        )}
      </>
    );
  };

  const renderMobileLayout = (property, bookNowProps) => (
    <>
      {/* Upper */}
      <Grid.Container>
        <Grid.Row>
          <Grid.Col>
            {/* Details */}
            {renderDetails(property, false)}
          </Grid.Col>
        </Grid.Row>
      </Grid.Container>

      {/* Book Now */}
      {renderBookNow(property, bookNowProps)}

      {/* Lower */}
      <Grid.Container>
        <Grid.Row>
          <Grid.Col>
            {/* Amenities */}
            {renderAmenities(property)}

            {/* Location */}
            {renderLocation(property)}

            {/* Affiliations */}
            {renderAffiliations(property)}
          </Grid.Col>
        </Grid.Row>
      </Grid.Container>
    </>
  );

  const renderDesktopLayout = (property, bookNowProps) => (
    <Grid.Container>
      <Grid.Row>
        {/* Left Column */}
        <Grid.Col xs={7}>
          {/* Details */}
          {renderDetails(property)}

          {/* Amenities */}
          {renderAmenities(property)}

          {/* Location */}
          {renderLocation(property)}

          {/* Affiliations */}
          {renderAffiliations(property)}
        </Grid.Col>

        {/* Right Column */}
        <Grid.Col xs={5}>
          {/* Book Now */}
          <VerticalSpacing size="sm">{renderBookNow(property, bookNowProps)}</VerticalSpacing>
        </Grid.Col>
      </Grid.Row>
    </Grid.Container>
  );

  const renderLayout = (propertyDetailsProps, bookNowProps) => {
    const { error: propertyDetailsError, loading: propertyDetailsLoading, property } = propertyDetailsProps;
    const { error: bookNowError, loading: bookNowLoading } = bookNowProps;

    // If either query is loading show a spinner
    // The spinner is only shown for book now while the component is mounted not on the server
    if (propertyDetailsLoading || (isMounted && bookNowLoading)) {
      return <Placeholder />;
    }

    // If either query fails we need to show an error
    if (propertyDetailsError || bookNowError) {
      // Handle property not found errors as 404
      const isNotFound =
        get(propertyDetailsError, 'graphQLErrors.[0].name') === 'PROPERTY_ERROR' ||
        get(bookNowError, 'graphQLErrors.[0].name') === 'PROPERTY_ERROR';

      if (isNotFound) {
        return <ErrorPage statusCode={404} />;
      }

      // All other errors 500
      return <ErrorPage statusCode={500} />;
    }

    // Try to find content that can be used for SEO
    const seoDescription = property.shortDescription;
    const seoImage = get(property, 'images.propertyImages[0].src') || get(property, 'images.cardImage') || null;
    const seoTitle = intl.formatMessage(messages.seoTitle, {
      propertyName: property.name
    });

    // Property found
    return (
      <PageWrapper style={{ maxWidth: '1600px' }}>
        <PageSEO
          title={seoTitle}
          description={seoDescription}
          facebookDescription={seoDescription}
          facebookImage={seoImage}
          facebookTitle={seoTitle}
          twitterDescription={seoDescription}
          twitterImage={seoImage}
          twitterTitle={seoTitle}
        />

        {/* Banner */}
        {property.images.propertyImages.length > 0 && (
          <BannerThree
            images={property.images.propertyImages}
            photoButtonText={intl.formatMessage(messages.viewPhotos)}
            onClickPhotoButton={() => {
              analytics.trackEvent('property-view-photos', {
                lodgeID: property.id,
                lodgeName: property.name,
                sectionName: 'Property Details'
              });
            }}
          />
        )}

        {/* This layout of this section of the page changes depending on what breakpoints you are in. */}
        <Grid.ScreenClassRender
          render={screenClass => {
            if (['xs', 'sm', 'md'].includes(screenClass)) {
              return renderMobileLayout(property, bookNowProps);
            }

            return renderDesktopLayout(property, bookNowProps);
          }}
        />

        {/* Similar Properties */}
        {property.similarProperties.length > 0 && (
          <>
            {/* Divider Line */}
            <Grid.Container>
              <Grid.Row>
                <Grid.Col>
                  <ContentDivider after theme="hr" />
                </Grid.Col>
              </Grid.Row>
            </Grid.Container>

            {/* Card Carousel */}
            <CardGridLayout.Wrapper
              title={intl.formatMessage(messages.similarPropertiesTitle)}
              cardData={property.similarProperties}
            />
          </>
        )}

        {/*
            The booking summary will un-stick when the bottom of the window
            reaches this element.
          */}
        <StickyEnd />
      </PageWrapper>
    );
  };

  const {
    query: { checkin, checkout }
  } = router;

  const slug = get(router, 'query.slug');

  return (
    <PropertyDetailsContainer slug={slug} dates={{ checkin, checkout }}>
      {propertyDetailsProps => (
        <BookNowContainer
          slug={slug}
          propertyTimeZone={propertyDetailsProps.property.timeZone}
          showError={errorMessage => dispatchErrorToast(errorMessage)}
        >
          {bookNowProps => renderLayout(propertyDetailsProps, bookNowProps)}
        </BookNowContainer>
      )}
    </PropertyDetailsContainer>
  );
}

export default compose(
  withAuth({ requireAuthentication: true }),
  withLocaleProvider,
  injectIntl,
  withRouting,
  withAnalytics()
)(PropertyDetails);

PropertyDetails.propTypes = {
  analytics: withAnalyticsPropType.isRequired,
  intl: intlShape.isRequired,
  router: withRoutingPropType.isRequired,
  auth: withAuthPropType.isRequired
};
